import { cn } from '@/lib/utils'
import Image from 'next/image'
import { useMemo } from 'react'

interface Rating {
  avg: number
  count: number
}
interface ProductInfoRatingProps {
  rating: Rating
  className?: string
  wrapperClassName?: string
  showText?: boolean
  total?: number
  showAvgText?: boolean
}

const normalizeRating = (rating: number, maxRating: number): number => {
  if (typeof rating !== 'number' || isNaN(rating)) return 0

  return rating > 5 ? Math.min(rating, maxRating * 2) / 2 : rating
}

export const ProductInfoRating = ({
  rating,
  className = '',
  wrapperClassName = '',
  showText = true,
  total,
  showAvgText = true,
}: ProductInfoRatingProps) => {
  const MAX_RATING = 5

  const stars = useMemo(() => {
    const avgRating = normalizeRating(rating.avg, MAX_RATING)

    const fullStars = Math.max(0, Math.floor(avgRating))
    const hasHalfStar = avgRating % 1 !== 0
    const emptyStars = Math.max(0, 5 - Math.ceil(avgRating))

    return {
      full: fullStars > 0 ? Array(fullStars).fill(null) : [],
      half: hasHalfStar ? [null] : [],
      empty: emptyStars > 0 ? Array(emptyStars).fill(null) : [],
    }
  }, [rating])

  if (!rating.avg) return null

  const scrollToReviews = () => {
    const reviewsSection = document.getElementById('product-reviews')
    if (reviewsSection) {
      reviewsSection.scrollIntoView({ behavior: 'smooth' })
    }
  }

  return (
    <div
      className={cn('flex items-center gap-1', wrapperClassName)}
      onClick={scrollToReviews}
    >
      <div className={cn('flex items-center gap-0.5', className)}>
        {stars.full.map((_, index) => (
          <Image
            key={`full-${index}`}
            src='/icons/icon-star.svg'
            width={20}
            height={20}
            alt='full-star'
          />
        ))}

        {stars.half.map((_, index) => (
          <Image
            key={`half-${index}`}
            src='/icons/icon-star-half.svg'
            width={20}
            height={20}
            alt='half-star'
          />
        ))}

        {stars.empty.map((_, index) => (
          <Image
            key={`empty-${index}`}
            src='/icons/icon-star.svg'
            width={20}
            height={20}
            alt='empty-star'
            className='opacity-15'
          />
        ))}
        {showAvgText && (
          <p className='ml-1 font-sans text-sm leading-4.5 text-neutral-600'>
            ({normalizeRating(rating.avg, MAX_RATING)})
          </p>
        )}
      </div>
      {showText && (
        <div className='flex items-center gap-2'>
          <p className='font-sans text-sm text-neutral-500'>
            ({' '}
            <span className='underline'>
              {total} <span className='max-lg:hidden'>đánh giá</span>
            </span>{' '}
            )
          </p>
        </div>
      )}
    </div>
  )
}
